import Router, { useRouter } from 'next/router'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { SpaceType } from 'src/components/SearchBar/types'
import { DEFAULT_ZOOM } from '../constants'
import { LocationType, SizeFilter, SortBy } from '../types/graphql'
import { IQueryParams, RedirectUrls } from '../types/types'

type IShallowOptional = {
  lat?: number
  lng?: number
  zoom?: number
  placeId?: string | undefined
  page?: number | undefined
}

type IShallowParams = {
  sliderOpen: boolean
  scrollY: string | undefined
}

type IUpdateParams = Partial<IQueryParams> & { sliderOpen?: boolean }

type IFilterContext = {
  isReady: boolean
  filters: IQueryParams
  updateParams: (args: IUpdateParams) => void
  filtersOpen: boolean
  setFiltersOpen: (open: boolean) => void
  shallowParams: IShallowParams
  shallowUpdateParams: (
    args: Partial<IShallowParams & IShallowOptional>
  ) => void
  isSliderOpen: boolean
  setIsSliderOpen: (open: boolean) => void
  showSqm: boolean
}

export const FilterContext = createContext<IFilterContext>({
  isReady: false,
  filters: {
    search: undefined,
    placeId: undefined,
    description: undefined,
    lat: undefined,
    lng: undefined,
    zoom: undefined,
    sortBy: undefined,
    minPrice: 0,
    maxPrice: 0,
    canStoreVehicle: undefined,
    sizes: undefined,
    page: 1,
    staticPage: undefined,
    spaceTypes: undefined,
    locationTypes: undefined,
    hood: undefined,
    city: undefined,
    zipcode: undefined,
    state: undefined
  },
  updateParams: (params: IUpdateParams): void => {
    const filteredParams = getFilteredParams({
      ...Router.query,
      ...params
    })
    Router.push({ pathname: RedirectUrls.Search, query: filteredParams })
  },
  filtersOpen: false,
  setFiltersOpen: () => {},
  shallowParams: { sliderOpen: false, scrollY: undefined },
  shallowUpdateParams: () => {},
  isSliderOpen: false,
  setIsSliderOpen: () => {},
  showSqm: false
})

const FilterProvider: React.FC<{
  children: React.ReactNode
  initialFilters?: Partial<IQueryParams>
}> = ({ children, initialFilters }) => {
  const value = useFilters(initialFilters)

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  )
}

export default React.memo(FilterProvider)

export const useFilterProvider = (): IFilterContext => useContext(FilterContext)

/**
 * Hook
 */

const useFilters = (initialFilters?: Partial<IQueryParams>): IFilterContext => {
  const router = useRouter()

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)

  const filters = useMemo<IQueryParams>(() => {
    let { lat, lng, zoom, placeId } = router.query
    const {
      search,
      description,
      sortBy,
      canStoreVehicle,
      sizes,
      maxPrice,
      minPrice,
      page,
      staticPage,
      spaceTypes,
      locationTypes,
      hood,
      city,
      zipcode,
      state
    } = router.query

    if (typeof window !== 'undefined' && !initialFilters && !router.isReady) {
      const urlParams = new URLSearchParams(window.location.search)
      lat =
        typeof urlParams.get('lat') === 'string'
          ? (urlParams.get('lat') as string)
          : undefined
      lng =
        typeof urlParams.get('lng') === 'string'
          ? (urlParams.get('lng') as string)
          : undefined
      zoom =
        typeof urlParams.get('zoom') === 'string'
          ? (urlParams.get('zoom') as string)
          : undefined
      placeId =
        typeof urlParams.get('placeId') === 'string'
          ? (urlParams.get('placeId') as string)
          : undefined
    }

    return {
      search: toStr(search),
      placeId: toStr(placeId),
      description: toStr(description),
      lat: toNum(lat),
      lng: toNum(lng),
      zoom: toNum(zoom) ?? DEFAULT_ZOOM,
      sortBy: toEnum<SortBy>(SortBy, sortBy),
      maxPrice: toNum(maxPrice),
      minPrice: toNum(minPrice),
      canStoreVehicle: toBool(canStoreVehicle),
      sizes: toEnumArray<SizeFilter>(SizeFilter, sizes),
      page: toNum(page) ?? 1,
      staticPage: toBool(staticPage),
      spaceTypes: toEnumArray<SpaceType>(SpaceType, spaceTypes),
      locationTypes: toEnumArray<LocationType>(LocationType, locationTypes),
      hood: toSlug(toStr(hood)),
      city: toSlug(toStr(city)),
      zipcode: toStr(zipcode),
      state: toStr(state),
      ...initialFilters,
      // User-controlled filter overrides — URL takes precedence over the
      // SSR-baked initialFilters for these keys so deep links from chips,
      // share URLs, etc. actually narrow the result set instead of being
      // silently overwritten by the pillar defaults.
      ...(sizes !== undefined && {
        sizes: toEnumArray<SizeFilter>(SizeFilter, sizes)
      }),
      ...(minPrice !== undefined && { minPrice: toNum(minPrice) }),
      ...(maxPrice !== undefined && { maxPrice: toNum(maxPrice) }),
      ...(sortBy !== undefined && {
        sortBy: toEnum<SortBy>(SortBy, sortBy)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, initialFilters])

  const shallowParams = useMemo<IShallowParams>(() => {
    let { sliderOpen, scrollY } = router.query
    if (typeof window !== 'undefined' && !initialFilters && !router.isReady) {
      const urlParams = new URLSearchParams(window.location.search)
      sliderOpen =
        typeof urlParams.get('sliderOpen') === 'string'
          ? (urlParams.get('sliderOpen') as string)
          : undefined
      scrollY =
        typeof urlParams.get('scrollY') === 'string'
          ? (urlParams.get('scrollY') as string)
          : undefined
    }
    return {
      sliderOpen: toBool(sliderOpen) ?? !!initialFilters,
      scrollY: toStr(scrollY) ?? undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, initialFilters])

  const shallowUpdateParams = useCallback(
    (params: Partial<IShallowParams & IShallowOptional>): void => {
      const newParams = {
        ...filters,
        ...shallowParams,
        ...initialFilters,
        ...params
      }
      const filteredParams = getFilteredParams(newParams)

      Router.push(
        {
          pathname: RedirectUrls.Search,
          query: filteredParams
        },
        undefined,
        // `scroll: false` — these pushes fire on every map drag / pinch
        // (debounced 300 ms). The Next.js default `scroll: true` would
        // scroll the window to top on each one, which on iOS Safari
        // toggles the URL-bar visibility, resizing the viewport and
        // visibly shifting the fixed-positioned map. Stay put.
        { shallow: true, scroll: false }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query]
  )

  const updateParams = useCallback(
    (params: IUpdateParams): void => {
      const newParams = {
        ...filters,
        ...initialFilters,
        ...params
      }
      const filteredParams = getFilteredParams(newParams)
      // See shallowUpdateParams above — same iOS URL-bar reasoning.
      // Filter changes shouldn't snap the page to top either; the search
      // results live in the (mounted) drawer / sticky map, not in body
      // scroll position.
      Router.push(
        { pathname: RedirectUrls.Search, query: filteredParams },
        undefined,
        { scroll: false }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query, filters, initialFilters]
  )

  // Seeded with the value the PRERENDERED HTML computed — deliberately NOT
  // `shallowParams.sliderOpen`. On /search (`getStaticProps`) `shallowParams`
  // falls back to `window.location.search` while `router.isReady` is false, so
  // it reads TRUE on the first client render while the static HTML computed
  // FALSE. That disagreement reaches the DOM through `MapWrapper`'s
  // `deferWhile` branch — which is deliberately NOT gated on `hasMounted` so
  // the static map lands in the initial HTML — and fires React #418
  // (`censusDelta: IMG:+1`, `/search`, firstDiffIndex 43002) on every
  // return-to-search link carrying `sliderOpen=true`
  // (`OnDemandSearchCard`, `OnDemandRecoveryCard`, `useDirectoryCardNav`,
  // `LocationSearchCard`). Adopt the URL's value one commit later instead.
  //
  // City/pillar pages are unaffected either way: they carry `initialFilters`,
  // so both sides already computed TRUE and the measured LCP deferral holds.
  const [isSliderOpen, setIsSliderOpen] = useState(!!initialFilters)

  useEffect(() => {
    setIsSliderOpen(shallowParams.sliderOpen)
    // Mount-only: every later change is a user action on the drawer itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(
    (): IFilterContext => ({
      isReady: router.isReady,
      filters,
      updateParams,
      filtersOpen,
      setFiltersOpen,
      shallowParams,
      shallowUpdateParams,
      isSliderOpen,
      setIsSliderOpen,
      showSqm:
        (!!filters.spaceTypes && getShowSqmSpaceTypes(filters.spaceTypes)) ||
        (!!filters.sizes && getShowSqmSizes(filters.sizes))
    }),
    [
      router.isReady,
      filters,
      updateParams,
      filtersOpen,
      shallowParams,
      shallowUpdateParams,
      isSliderOpen,
      setIsSliderOpen
    ]
  )

  return value
}

/**
 * Helpers
 */

function toStr(arg: string | string[] | undefined): string | undefined {
  if (typeof arg === 'string') return arg
  return undefined
}

/** Boundary normalization for city/hood slug params. Google-indexed legacy
 * URLs carry slugs built from comma-bearing address components
 * (`?city=guadalajara-&hood=toluquilla-`) and accented variants
 * (`san-pedro-garza-garcía`). Stray hyphens + diacritics make
 * SEARCH_PAGE_CITY_MAP / HOOD_MAP lookups miss and fragment analytics into
 * per-variant city rows. The param never reaches the backend search (which
 * runs on lat/lng + filters), so normalizing here is render/analytics-safe. */
function toSlug(arg: string | undefined): string | undefined {
  if (!arg) return undefined
  const normalized = arg
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/^-+|-+$/g, '')
  return normalized || undefined
}

function toEnum<T>(
  enumType: Record<string, T>,
  arg: string | string[] | undefined
): T | undefined {
  if (typeof arg === 'string') {
    const enumValues = Object.values(enumType)
    if (enumValues.includes(arg as T)) {
      return arg as unknown as T
    }
  }
  return undefined
}

function toEnumArray<T>(
  enumType: Record<string, T>,
  arg: string | string[] | undefined
): T[] | undefined {
  if (typeof arg === 'string') {
    const enumValues = Object.values(enumType)
    // Handle comma-separated strings like "SMALL,MEDIUM,LARGE"
    const values = arg.split(',').map((v) => v.trim())
    const validValues = values.filter((value) =>
      enumValues.includes(value as T)
    ) as T[]
    return validValues.length > 0 ? validValues : undefined
  }

  if (Array.isArray(arg)) {
    const enumValues = Object.values(enumType)
    const values = arg.filter((value) => enumValues.includes(value as T)) as T[]
    const uniqueValues = [...new Set(values)]
    return uniqueValues.length > 0 ? uniqueValues : undefined
  }

  return undefined
}

function toNum(arg: string | string[] | undefined): number | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof arg === 'string' && !isNaN(arg as any)) return Number(arg)
  return undefined
}

function toBool(arg: string | string[] | undefined): boolean | undefined {
  if (typeof arg === 'string') {
    return arg === 'true' ? true : arg === 'false' ? false : undefined
  }
  return undefined
}

function getFilteredParams(
  params: Partial<IQueryParams> & { sliderOpen?: boolean; scrollY?: string }
) {
  return Object.entries(params).reduce(
    (
      memo: {
        [key: string]: string | number | [number, number] | boolean | string[]
      },
      [key, value]
    ) => {
      if (value !== undefined && value !== null && value !== '') {
        memo[key] = value
      }

      return memo
    },
    {}
  )
}

function getShowSqmSpaceTypes(spaceTypes: SpaceType[] | undefined): boolean {
  if (!spaceTypes || spaceTypes.length === 0) return false
  if (spaceTypes.length > 2) return false
  const VALID_SPACE_TYPES = [
    SpaceType.INDUSTRIAL_WAREHOUSE,
    SpaceType.WAREHOUSE
  ]
  return spaceTypes.every((type) => VALID_SPACE_TYPES.includes(type))
}

function getShowSqmSizes(sizes: SizeFilter[] | undefined): boolean {
  if (!sizes || sizes.length === 0) return false
  if (sizes.includes(SizeFilter.Any)) return false
  if (sizes.length > 3) return false
  const VALID_SIZES = [
    SizeFilter.Commercial,
    SizeFilter.Industrial,
    SizeFilter.Nave
  ]
  return sizes.every((size) => VALID_SIZES.includes(size))
}
